import { GetStaticPaths } from 'next'
import { paths } from '@/constants/paths'
import { ProjectMetaProvider } from '@/contexts/ProjectMetaContext'
import { getBasicPageMetaData, Seo } from '@/layout/Seo'
import { buildBrandedCloudinaryImageTransforms } from '@/layout/Seo/SeoHelper'
import { HideTopNavigationLayout, NextPageWithLayout } from '@/layouts'
import { MarketingVideoPopup } from '@/organisms/MarketingVideoPopup'
import { TellYourStoryPromptProvider } from '@/organisms/TellYourStoryPrompt'
import { Captcha } from '@/services/Captcha'
import { createGuildDeepLink } from '@/services/DeepLinkService'
import { getOfferById, Offer } from '@/services/PifService'
import { getProjectMeta, ProjectMeta } from '@/services/ProjectsService'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { PayItForwardProjectCheckoutOfferView } from '@/views/PayItForward/PayItForwardProjectCheckoutOfferView'
import { PayItForwardCheckoutProvider } from '@/views/PayItForward/PayItForwardProjectCheckoutOfferView/usePayItForwardState/usePayItForwardState'

interface PayItForwardProjectCheckoutOfferPageParams {
  cloudinaryImageTransformation: string
  locale: string
  offer: Offer
  guildDeepLink: string
  projectMeta: ProjectMeta
}

export const PayItForwardProjectCheckoutOfferPage: NextPageWithLayout<PayItForwardProjectCheckoutOfferPageParams> = ({
  cloudinaryImageTransformation,
  locale,
  offer,
  guildDeepLink,
  projectMeta,
}) => {
  const { t } = useTranslate('pif')
  const projectName = projectMeta.name
  return (
    <div className="bg-white">
      <Captcha />
      <Seo
        {...getBasicPageMetaData({
          cloudinaryImagePath: projectMeta.discoveryPosterLandscapeCloudinaryPath,
          cloudinaryTransformation: cloudinaryImageTransformation,
          description:
            projectMeta.pifExplainerText ||
            t('keepItFree', `Keep {{projectName}} free and help create future seasons.`, { projectName }),
          locale,
          path: `${paths.payItForward.index}/${projectMeta.slug}/checkout/${offer.id}`,
          title: t('payItForwardForProject', 'Pay it Forward for {{projectName}} on Angel Studios', { projectName }),
        })}
      />
      <ProjectMetaProvider project={projectMeta}>
        <TellYourStoryPromptProvider projectSlug={projectMeta.slug} projectName={projectMeta.name}>
          <PayItForwardCheckoutProvider>
            <MarketingVideoPopup />
            <PayItForwardProjectCheckoutOfferView
              projectSlug={projectMeta.slug}
              offer={offer}
              name={projectMeta.name}
              guildDeepLink={guildDeepLink}
            />
          </PayItForwardCheckoutProvider>
        </TellYourStoryPromptProvider>
      </ProjectMetaProvider>
    </div>
  )
}

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

interface GetStaticPropsPathParams {
  projectSlug: string
  offerId: string
}

export const getStaticProps = getStaticPropsErrorHandler(
  { filePath: 'pages/[locale]/pay-it-forward/[projectSlug]/checkout/[offerId]/index.tsx' },
  async ({ federationClient, locale, next404, params, preview }) => {
    const { projectSlug, offerId } = params as unknown as GetStaticPropsPathParams

    const offer = await getOfferById({ projectSlug, offerId }, federationClient)
    if (!offer) {
      return next404()
    }

    const projectMeta = await getProjectMeta({ slug: params?.projectSlug as string }, federationClient, {
      locale,
      preview,
    })
    const cloudinaryImageTransformation = buildBrandedCloudinaryImageTransforms({
      logoCloudinaryPath: projectMeta.logoCloudinaryPath,
    })

    const guildDeepLink = await createGuildDeepLink(
      {
        route: 'Guild',
        path: '/home#download',
      },
      {
        campaign: 'guild-web',
        feature: 'pif-guild-upsell',
        channel: 'angel-web',
      },
    )

    return {
      props: {
        guildDeepLink,
        locale,
        projectMeta,
        offer,
        cloudinaryImageTransformation,
        ...(await loadTranslations(locale, ['common', 'pif', 'account'])),
      },
    }
  },
)

PayItForwardProjectCheckoutOfferPage.getLayout = (page) => {
  return <HideTopNavigationLayout isDarkMode={false}>{page}</HideTopNavigationLayout>
}

export default PayItForwardProjectCheckoutOfferPage
